/**
 * BENCHMARK-INFO.MODAL
 * Technical overview of the benchmark question calculation pipeline
 */

import Modal from "@/components/modal"
import Card from "@/components/card"
import ListFields from "@/components/list-fields"
import ListItem from "@/components/list-item"
import ListCell from "@/components/list-cell"
import { CALC_QUESTION_PARAMS_DEFAULT, CalcQuestionParams } from "@/features/benchmark/entities/question/model/benchmarkQuestion.types"

interface Props {
  onClose: () => void
  params?: CalcQuestionParams
}

export default function BenchmarkInfoModal({ onClose, params = CALC_QUESTION_PARAMS_DEFAULT }: Props) {
  const rpa = params.minResponsesPerAccount ?? 10
  const mag = params.minAccountsGlobal ?? 5

  return (
    <Modal isFullScreen onClose={onClose} title="Comment fonctionne le calcul ?">
      <div className="space-y-6">
        <div>
          <p className="text-sm grey-t mb-3">
            Le calcul reconstruit les lignes benchmark question depuis les données brutes de l&apos;archive, en 3
            étapes. Le regroupement lui-même (quel texte appartient à quelle question de la banque) n&apos;est pas
            fait ici — il vient de <code className="bg-gray-100 px-1 rounded text-xs">dim_questions.bank_id</code>,
            posé par le seed/élargissement de la banque (archive.bank_questions).
          </p>
          <div className="flex items-center gap-2 text-sm grey-t flex-wrap">
            {["fact_notes", "→ score / compte", "→ seuil global", "→ regroupement secteur"].map((step, i) => (
              <span
                key={i}
                className={`px-2 py-0.5 rounded text-xs ${i === 0 ? "bg-blue-50 text-blue-700" : "bg-gray-50"}`}>
                {step}
              </span>
            ))}
          </div>
        </div>

        <Card isWithoutPadding isNotCardClass>
          <ListFields>
            <ListCell width={200} text="Étape" />
            <ListCell isFlex text="Détail" />
          </ListFields>

          <ListItem className="items-start py-3">
            <ListCell width={200}>
              <div className="text-sm font-medium">1. Score / compte</div>
            </ListCell>
            <ListCell isFlex>
              <div className="flex flex-col gap-1">
                <div className="text-sm">
                  Pour chaque (<code className="bg-gray-100 px-1 rounded text-xs">bank_id</code> × compte), on agrège
                  toutes les réponses des questions archivées liées à ce{" "}
                  <code className="bg-gray-100 px-1 rounded text-xs">bank_id</code> et on calcule la moyenne 0-100. Un
                  compte est inclus seulement s&apos;il a ≥ {rpa} réponses.
                </div>
                <div className="text-sm grey-t">Ex : Compte A, bank_id X → 42 réponses, moyenne 73.2</div>
              </div>
            </ListCell>
          </ListItem>

          <ListItem className="items-start py-3">
            <ListCell width={200}>
              <div className="text-sm font-medium">2. Seuil global</div>
            </ListCell>
            <ListCell isFlex>
              <div className="flex flex-col gap-1">
                <div className="text-sm">
                  Si un <code className="bg-gray-100 px-1 rounded text-xs">bank_id</code> a moins de {mag} comptes
                  valides, il est ignoré — aucune ligne dans le catalogue.
                </div>
                <div className="text-sm grey-t">
                  Ex avec seuil = {mag} : {mag - 1} comptes → exclu, {mag + 3} comptes → inclus.
                </div>
              </div>
            </ListCell>
          </ListItem>

          <ListItem className="items-start py-3">
            <ListCell width={200}>
              <div className="text-sm font-medium">3. Regroupement secteur</div>
            </ListCell>
            <ListCell isFlex>
              <div className="flex flex-col gap-1">
                <div className="text-sm">
                  Pour chaque <code className="bg-gray-100 px-1 rounded text-xs">bank_id</code> inclus, une ligne{" "}
                  <code className="bg-gray-100 px-1 rounded text-xs">all/all</code> (toutes entreprises) est créée,
                  plus une ligne par secteur représenté (
                  <code className="bg-gray-100 px-1 rounded text-xs">tech/all</code>,{" "}
                  <code className="bg-gray-100 px-1 rounded text-xs">commerce/all</code>…). Pas de notion de taille
                  d&apos;entreprise — l&apos;échantillon est trop réduit pour être fiable à ce niveau.
                </div>
                <div className="text-sm grey-t">
                  Le dashboard compare une entreprise à son propre secteur si celui-ci est renseigné, sinon à toutes les
                  entreprises confondues.
                </div>
              </div>
            </ListCell>
          </ListItem>
        </Card>

        <Card isWithoutPadding isNotCardClass>
          <ListFields>
            <ListCell width={220} text="Paramètre" />
            <ListCell width={80} text="Valeur" />
            <ListCell isFlex text="Effet concret" />
          </ListFields>

          <ListItem className="items-start py-2">
            <ListCell width={220}>
              <div className="text-sm">Réponses min / entreprise</div>
            </ListCell>
            <ListCell width={80}>
              <div className="text-sm font-medium tabular-nums">{rpa}</div>
            </ListCell>
            <ListCell isFlex>
              <div className="text-sm grey-t">
                Une entreprise avec moins de {rpa} réponses sur une question est exclue — moyenne trop instable et
                risque d&apos;exposition individuelle.
              </div>
            </ListCell>
          </ListItem>

          <ListItem className="items-start py-2">
            <ListCell width={220}>
              <div className="text-sm">Entreprises min pour inclure</div>
            </ListCell>
            <ListCell width={80}>
              <div className="text-sm font-medium tabular-nums">{mag}</div>
            </ListCell>
            <ListCell isFlex>
              <div className="text-sm grey-t">
                Une question vue par moins de {mag} entreprises valides n&apos;entre pas dans le catalogue. Détermine la
                taille du catalogue.
              </div>
            </ListCell>
          </ListItem>
        </Card>
      </div>
    </Modal>
  )
}
